import React, { useState, useEffect } from 'react';
import PageLayout from '../components/common/PageLayout';
import { Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';

/**
 * Users Page
 * Displays and manages users in the organization
 */
const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get CSRF token from cookie
  const getCsrfToken = () => {
    const cookies = document.cookie.split(';');
    const csrfCookie = cookies.find(c => c.trim().startsWith('csrftoken='));
    return csrfCookie ? csrfCookie.split('=')[1] : '';
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        // TODO: Replace with actual API endpoint
        // const response = await axios.get('/api/users/', {
        //   withCredentials: true,
        //   headers: {
        //     'Accept': 'application/json',
        //   },
        // });
        // setUsers(response.data || []);
        
        // Sample data for now
        setUsers([
          { id: 1, username: 'john.doe', email: 'john.doe@example.com', fullName: 'John Doe', role: 'admin', status: 'active', lastLogin: '2024-01-15' },
          { id: 2, username: 'jane.smith', email: 'jane.smith@example.com', fullName: 'Jane Smith', role: 'manager', status: 'active', lastLogin: '2024-01-14' },
          { id: 3, username: 'bob.wilson', email: 'bob.wilson@example.com', fullName: 'Bob Wilson', role: 'staff', status: 'inactive', lastLogin: '2024-01-10' },
        ]);
        setError(null);
      } catch (err) {setError('Failed to load users. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleAddUser = () => {
    // TODO: Implement add user functionality
  };

  const handleEditUser = (userId) => {
    // TODO: Implement edit user functionality
  };

  const handleDeleteUser = (userId) => {
    // TODO: Implement delete user functionality
    if (window.confirm('Are you sure you want to delete this user?')) {}
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'default';
  };

  const getRoleColor = (role) => {
    const roleColors = {
      admin: 'error',
      manager: 'warning',
      staff: 'info',
    };
    return roleColors[role] || 'default';
  };

  return (
    <PageLayout
      title="Users"
      showCard={false}
      breadcrumbs={[
        { label: 'Home', path: '/dashboard/' },
        { label: 'Users' },
      ]}
      navLinks={[
        { url: '/dashboard/', label: 'Dashboard' },
        { url: '/onboarding/landing/', label: 'Landing' },
        { url: '/finance/rates/', label: 'Manage Rate' },
      ]}
    >
      <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  User Management
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddUser}
                  sx={{ textTransform: 'none' }}
                >
                  Add User
                </Button>
              </Box>

              {error && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
                  {error}
                </Box>
              )}

              {loading ? (
                <Typography>Loading users...</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Username</strong></TableCell>
                        <TableCell><strong>Full Name</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Role</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Last Login</strong></TableCell>
                        <TableCell align="right"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography color="text.secondary">No users found</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map((user) => (
                          <TableRow key={user.id} hover>
                            <TableCell>{user.username}</TableCell>
                            <TableCell>{user.fullName}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Chip
                                label={user.role}
                                color={getRoleColor(user.role)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={user.status}
                                color={getStatusColor(user.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{user.lastLogin || 'Never'}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleEditUser(user.id)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteUser(user.id)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
    </PageLayout>
  );
};

export default UsersPage;

